import { useRef, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const lines = ["Something", "big is", "coming."];

const ParticleField = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h;
    const mouse = { x: -9999, y: -9999 };
    const resize = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.x = e.clientX - r.left;
      mouse.y = e.clientY - r.top;
    };
    window.addEventListener("mousemove", onMove);
    const N = 90;
    const pts = Array.from({ length: N }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.0004,
      vy: (Math.random() - 0.5) * 0.0004,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > 1) p.vx *= -1;
        if (p.y < 0 || p.y > 1) p.vy *= -1;
        const px = p.x * w;
        const py = p.y * h;
        const d = Math.hypot(px - mouse.x, py - mouse.y);
        const near = d < 160;
        ctx.beginPath();
        ctx.arc(px, py, near ? 1.8 : 1, 0, Math.PI * 2);
        ctx.fillStyle = near ? "rgba(0,240,255,0.9)" : "rgba(248,249,250,0.25)";
        ctx.fill();
      });
      for (let i = 0; i < N; i++) {
        for (let j = i + 1; j < N; j++) {
          const dx = (pts[i].x - pts[j].x) * w;
          const dy = (pts[i].y - pts[j].y) * h;
          const d = Math.hypot(dx, dy);
          if (d < 110) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x * w, pts[i].y * h);
            ctx.lineTo(pts[j].x * w, pts[j].y * h);
            ctx.strokeStyle = `rgba(0,240,255,${0.08 * (1 - d / 110)})`;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
};

export const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={sectionRef} data-testid="hero-section" className="min-h-screen relative flex flex-col justify-center overflow-hidden pt-20">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <ParticleField />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#030305]" />
      </motion.div>
      <div className="scanline" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          data-testid="hero-overline"
          className="text-xs font-mono-z uppercase tracking-[0.4em] text-[#00f0ff] mb-8"
        >
          [ Transmission 001 — Zavico.net ]
        </motion.p>

        <h1 data-testid="hero-title" className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[0.95] tracking-tight">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.5 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 2 ? (
                  <>
                    <span className="text-[#00f0ff]">{line.slice(0, -1)}</span>
                    <span className="text-outline">.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.9 }}
          data-testid="hero-subtitle"
          className="mt-8 max-w-md text-sm sm:text-base text-[#8e8ea0] leading-relaxed font-mono-z"
        >
          We are not ready to tell you what it is. You are not ready to know. But when it arrives, everything shifts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.45, duration: 0.9 }}
          className="mt-12 flex items-center gap-6"
        >
          <a
            href="#notify"
            data-testid="hero-cta-btn"
            className="group inline-flex items-center gap-3 border border-[#00f0ff]/50 text-[#00f0ff] font-mono-z uppercase tracking-[0.25em] text-xs px-8 py-4 transition-colors duration-300 hover:bg-[#00f0ff] hover:text-[#030305]"
          >
            Request Access
            <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
          <span className="text-xs font-mono-z text-[#8e8ea0] uppercase tracking-[0.2em] hidden sm:block">Scroll to decode</span>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-px h-14 bg-gradient-to-b from-[#00f0ff]/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
